// src/pages/BookingPage.jsx
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { StepBar, TenantUnavailable } from "../components/ui";
import { useBooking, fetchBookedSlots } from "../hooks/useBooking";
import ServiceStep        from "../components/booking/ServiceStep";
import BarberStep         from "../components/booking/BarberStep";
import DateTimeStep       from "../components/booking/DateTimeStep";
import DetailsStep        from "../components/booking/DetailsStep";
import PaymentStep        from "../components/booking/PaymentStep";
import ConfirmationScreen from "../components/booking/ConfirmationScreen";
import WaitlistModal      from "../components/booking/WaitlistModal";
import { Loader2, AlertCircle } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { useAuth } from "../context/AuthContext";
import { toDateStrInTz } from "../utils/dateUtils";

const BASE_STEPS    = ["Service", "Team Member", "Date & Time", "Your Info"];
const PAYMENT_STEPS = ["Service", "Team Member", "Date & Time", "Your Info", "Payment"];

export default function BookingPage() {
  const { tenant } = useTenant();
  const { user } = useAuth();

  const requiresPayment = tenant?.paymentMode === "online" && tenant?.stripeConnectStatus === "active";
  const STEPS = requiresPayment ? PAYMENT_STEPS : BASE_STEPS;

  const {
    booking, bookingId, step, confirmed, saving, emailSent, bookingError,
    setStep, updateBooking, updateCustomer,
    prefillFromUser, canProceed, confirmBooking, reset,
  } = useBooking();

  const [bookedSlots,  setBookedSlots]  = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [waitlistSlot, setWaitlistSlot] = useState(null);
  const [searchParams]                  = useSearchParams();
  const navigate                        = useNavigate();
  const { slug }                        = useParams();

  useEffect(() => { prefillFromUser(); }, [prefillFromUser]);

  // Pre-fill from a deep link:
  //   ?service=id          → storefront service click: select it and jump to Team
  //   ?barber=id           → storefront team click: pre-select that staff member
  //   ?barber=id&date=…&time=…  → waitlist notification: also pre-fill date/time
  useEffect(() => {
    if (!tenant) return;
    const serviceId = searchParams.get("service");
    const barberId  = searchParams.get("barber");
    const dateParam = searchParams.get("date");
    const timeParam = searchParams.get("time");

    if (serviceId) {
      const svc = (tenant.services || []).find(s => s.id === serviceId);
      if (svc) { updateBooking("service", svc); setStep(2); }
    }

    if (barberId) {
      const staffMember = (tenant.staff || []).find(s => s.id === barberId);
      if (staffMember) {
        updateBooking("barber", staffMember);
        if (dateParam) {
          const [y, m, d] = dateParam.split("-").map(Number);
          updateBooking("date", new Date(y, m - 1, d));
          if (timeParam) updateBooking("time", timeParam);
        }
      }
    }
  }, [tenant?.id]);  // eslint-disable-line

  // Fetch real availability whenever barber or date changes
  useEffect(() => {
    if (!booking.barber || !booking.date) {
      setBookedSlots([]);
      return;
    }
    setLoadingSlots(true);
    fetchBookedSlots(booking.barber.id, toDateStrInTz(booking.date, tenant?.timezone || "America/New_York"))
      .then(setBookedSlots)
      .finally(() => setLoadingSlots(false));
  }, [booking.barber, booking.date]);

  const dateStr = booking.date
    ? toDateStrInTz(booking.date, tenant?.timezone || "America/New_York")
    : null;

  // Billing lockout gate — all hooks already called above. Blocks new bookings
  // while a tenant's trial has ended / subscription is cancelled.
  if (tenant?.billingLocked) return <TenantUnavailable businessName={tenant.name} />;

  // Guest booking gate — all hooks already called above
  if ((tenant?.features?.guestBooking ?? true) === false && !user) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-surface-100 shadow-sm p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="font-semibold text-xl text-ink-900 mb-2">Sign in to book</h2>
          <p className="text-ink-400 text-sm font-sans mb-6">
            {tenant?.name} requires an account to make a booking.
          </p>
          <a href="/login" className="btn-primary w-full block">Sign in</a>
          <a href="/signup" className="block mt-3 text-sm text-brand-600 hover:underline font-sans">
            Create an account
          </a>
        </div>
      </div>
    );
  }

  if (confirmed) return <ConfirmationScreen booking={booking} bookingId={bookingId} emailSent={emailSent} onReset={reset} />;

  function handleNextFromDetails() {
    if (requiresPayment) {
      setStep(5);
    } else {
      confirmBooking();
    }
  }

  return (
    <div className="min-h-screen bg-surface-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-32">
        <StepBar current={step} steps={STEPS} />

        <div className="animate-slide-up">
          {step === 1 && <ServiceStep  selected={booking.service} onSelect={s => updateBooking("service", s)} />}
          {step === 2 && <BarberStep   selected={booking.barber}  onSelect={b => updateBooking("barber",  b)} />}
          {step === 3 && (
            <DateTimeStep
              selectedDate={booking.date}
              selectedTime={booking.time}
              onDateSelect={d => updateBooking("date", d)}
              onTimeSelect={t => updateBooking("time", t)}
              businessHours={tenant?.businessHours || null}
              bookedSlots={bookedSlots}
              loadingSlots={loadingSlots}
              barberAvailability={booking.barber?.availability || null}
              timezone={tenant?.timezone || "America/New_York"}
              onWaitlist={booking.barber && dateStr ? (slot) => setWaitlistSlot(slot) : null}
            />
          )}
          {step === 4 && <DetailsStep booking={booking} onUpdate={updateCustomer} />}
          {step === 5 && requiresPayment && (
            <PaymentStep
              booking={booking}
              tenantId={tenant?.id}
              onSuccess={confirmBooking}
            />
          )}
        </div>

        {bookingError && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50
                          border border-red-200 rounded-xl px-4 py-3">
            <AlertCircle size={16} className="shrink-0" />
            {bookingError}
          </div>
        )}
      </div>

      {/* Sticky bottom action bar — stays pinned to the viewport bottom so the
          Back/Continue actions are always reachable, on short and long steps alike.
          Hidden on the payment step (PaymentStep has its own submit). */}
      <div className="sticky bottom-0 z-20 border-t border-surface-200 bg-surface-50/90
                      backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
        {/* Fade so scrolling content reads as continuing behind the bar */}
        <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t
                        from-surface-50 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {step !== 5 && (
            <div className="flex items-center justify-between">
              {step > 1
                ? <button className="btn-secondary" onClick={() => setStep(s => s - 1)}>← Back</button>
                : <button className="btn-secondary" onClick={() => navigate(`/${slug}`)}>← Back</button>
              }
              {step < 4
                ? <button className="btn-primary" disabled={!canProceed()} onClick={() => setStep(s => s + 1)}>
                    Continue →
                  </button>
                : <button className="btn-primary min-w-[180px] flex items-center justify-center gap-2"
                    disabled={!canProceed() || saving} onClick={handleNextFromDetails}>
                    {saving
                      ? <><Loader2 size={16} className="animate-spin" /> Confirming...</>
                      : requiresPayment ? "Continue to Payment →" : "Confirm Booking ✓"
                    }
                  </button>
              }
            </div>
          )}
          {step === 5 && (
            <button className="btn-secondary" onClick={() => setStep(4)}>← Back</button>
          )}
        </div>
      </div>

      {waitlistSlot && dateStr && booking.barber && (
        <WaitlistModal
          tenantId={tenant?.id}
          barberId={booking.barber.id}
          dateStr={dateStr}
          time={waitlistSlot}
          onClose={() => setWaitlistSlot(null)}
        />
      )}
    </div>
  );
}
